<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { Pencil } from 'lucide-svelte';
  import { route } from '$lib/state/route.svelte';
  import { ticketsApi } from '$lib/api/tickets';
  import { relTime, pretty, projKind, teamKind, teamLabel } from '$lib/utils/format';
  import Avatar from '$lib/components/ui/Avatar.svelte';
  import Chip from '$lib/components/ui/Chip.svelte';
  import type { AdminUser, Deploy, Project } from '$lib/types';

  const {
    project,
    devs,
    lastDeploy,
    onEdit
  }: { project: Project; devs: AdminUser[]; lastDeploy: Deploy | null; onEdit: (p: Project) => void } = $props();

  // ponytail: one open-ticket-count query per card (N+1 across the grid).
  // Fine at expected project-catalog scale (tens, not thousands); if that
  // changes, move this into a single bulk endpoint.
  const ticketsQuery = createQuery(() => ({
    queryKey: ['project-tickets', project.id],
    queryFn: () => ticketsApi.forProject(project.id)
  }));
  const openCount = $derived(ticketsQuery.data?.results.filter((t) => t.status !== 'resolved' && t.status !== 'closed').length ?? 0);

  function open() {
    route.push({ tab: 'project', projectId: project.id });
  }
</script>

<div class="card proj-card" onclick={open} role="button" tabindex="0" onkeydown={(e) => e.key === 'Enter' && open()}>
  <div style="display:flex;align-items:flex-start;gap:10px">
    <div style="flex:1">
      <div class="pc-title">{project.name}</div>
    </div>
    <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px">
      <Chip label={pretty(project.status)} kind={projKind(project.status)} dot />
      <Chip label={teamLabel(project.team)} kind={teamKind(project.team)} />
    </div>
  </div>
  <div class="divider"></div>
  <div class="pc-row">
    <span class="pc-lbl">Developers</span>
    {#if devs.length}
      <div style="display:flex;align-items:center;gap:8px">
        <div class="av-stack">
          {#each devs as d (d.id)}
            <Avatar id={d.id} name={d.name || d.email} size={24} />
          {/each}
        </div>
        <span style="color:var(--t2);font-size:12.5px">{devs.map((d) => (d.name || d.email).split(' ')[0]).join(', ')}</span>
      </div>
    {:else}
      <span style="color:var(--t3)">None granted</span>
    {/if}
  </div>
  <div class="pc-row">
    <span class="pc-lbl">Open tickets</span>
    <span style="color:var(--t1);font-weight:600">{openCount}</span>
  </div>
  <div class="pc-row">
    <span class="pc-lbl">Last deploy</span>
    {#if lastDeploy}
      <span style="display:flex;align-items:center;gap:7px">
        <span class="mono">{lastDeploy.sha}</span>
        <span style="color:var(--t3);font-size:12px">{relTime(lastDeploy.created_at)}</span>
      </span>
    {:else}
      <span style="color:var(--t3)">—</span>
    {/if}
  </div>
  <div style="display:flex;gap:8px;margin-top:2px">
    <button type="button" class="btn btn-secondary btn-sm" style="flex:1;justify-content:center" onclick={(e) => { e.stopPropagation(); open(); }}>
      Open board
    </button>
    <button type="button" class="btn btn-secondary btn-sm" onclick={(e) => { e.stopPropagation(); onEdit(project); }} aria-label="Edit project">
      <Pencil size={15} />
    </button>
  </div>
</div>
